import { createNativeBridgeManifest } from '@padlet/native-bridge-core'
import { z } from 'zod'
import { ApplicationMenuActionResultSchema, ApplicationMenuModelSchema } from './applicationMenu'
import { DESKTOP_SHELL_PROTOCOL_VERSION } from './capabilities'
import {
  PurchaseStoreProductSchema,
  StoreOperationReplySchema,
  StoreProductsRequestSchema,
  StoreProductsSchema,
  StorePurchaseSettlementReplySchema,
  StorePurchaseSettlementSchema,
} from './storePurchases'

const ShellHelloSchema = z
  .object({
    protocolVersion: z.literal(DESKTOP_SHELL_PROTOCOL_VERSION),
  })
  .strict()

const ApplicationMenuModelAcknowledgementSchema = z
  .object({
    revision: z.number().int().nonnegative(),
  })
  .strict()

const ClearApplicationMenuModelSchema = z
  .object({
    revision: z.number().int().nonnegative(),
  })
  .strict()

const InvokeApplicationMenuActionSchema = z
  .object({
    actionId: z.string(),
    revision: z.number().int().nonnegative(),
  })
  .strict()

const EmptyPayloadSchema = z.object({}).strict()

/**
 * A true reply means the page is on the make view, so the shell must not reload the tab.
 * A page that cannot switch must send an error or no reply so the reload fallback runs.
 */
const MakePageSwitchSchema = z.object({ switched: z.literal(true) }).strict()

function parseWith<TSchema extends z.ZodTypeAny>(schema: TSchema) {
  return (value: z.input<TSchema>): z.output<TSchema> => schema.parse(value)
}

export const desktopShellManifest = createNativeBridgeManifest({
  webToNative: {
    closeCurrentTab: {
      payload: parseWith(EmptyPayloadSchema),
      reply: parseWith(z.undefined()),
    },
    replaceApplicationMenuModel: {
      payload: parseWith(ApplicationMenuModelSchema),
      reply: parseWith(ApplicationMenuModelAcknowledgementSchema),
    },
    clearApplicationMenuModel: {
      payload: parseWith(ClearApplicationMenuModelSchema),
      reply: parseWith(z.undefined()),
    },
    updateLocale: {
      payload: parseWith(EmptyPayloadSchema),
      reply: parseWith(z.undefined()),
    },
    getStoreProducts: {
      payload: parseWith(StoreProductsRequestSchema),
      reply: parseWith(StoreProductsSchema),
    },
    purchaseStoreProduct: {
      payload: parseWith(PurchaseStoreProductSchema),
      reply: parseWith(StoreOperationReplySchema),
    },
    restoreStorePurchases: {
      payload: parseWith(EmptyPayloadSchema),
      reply: parseWith(StoreOperationReplySchema),
    },
  },
  nativeToWeb: {
    shellHello: {
      payload: parseWith(ShellHelloSchema),
      reply: parseWith(ShellHelloSchema),
    },
    invokeApplicationMenuAction: {
      payload: parseWith(InvokeApplicationMenuActionSchema),
      reply: parseWith(ApplicationMenuActionResultSchema),
    },
    showMakePage: {
      payload: parseWith(EmptyPayloadSchema),
      reply: parseWith(MakePageSwitchSchema),
    },
    settleStorePurchase: {
      payload: parseWith(StorePurchaseSettlementSchema),
      reply: parseWith(StorePurchaseSettlementReplySchema),
    },
  },
})
